import React, { useEffect, useRef, useState } from "react";

import { Text, View, Image, ScrollView, Platform, TouchableOpacity, ViewStyle, ImageStyle } from "react-native";

import * as Navigation from "../../../specific/utils/Navigation/Navigation";
import AppTheme from "../../utils/Theme";
import ActivityIndicator from "../../designSystem/ActivityIndicator/ActivityIndicator";
import { EventFiltersByLevelChanged, ScreenActivitySheet, ScreenCategoryHome, ScreenPDFViewer, superPowersKeys } from "../../utils/Constants";
import Images from "../../../specific/utils/Images";
import i18n, { getLanguage } from "../../utils/Localization/Localization";
import Style from "../../utils/Style";
import Activity from "../../models/Activity";
import { useNavigate } from "../../../specific/utils/Navigation/Navigation";
import User from "../../data/user/User";
import ActionSheet from "../ActionSheets/ActionSheet";
import InViewModal from "../../../specific/components/InViewModal/InViewModal";
import { localizedString } from "../Activities/ActivitySheet";
import Category from "../../models/Category";
import Modal from "../../../specific/components/Modal/Modal";
import DocumentsViewer from "../Documents/DocumentsViewer";
import config from "../../../../config";
import { IcBack, IcChevronLeft, IcChevronRight, IcClose, IcDev, IcFilm, IcFilter, IcHeadphones, IcLightBulb } from "../../utils/Icons";
import SVGView from "../../utils/SvgView";
import SuperPowerRow from "./SuperPowerRow";
import UserLevelSelection from "../Filters/UserLevelSelection";
import { build } from "../../../../package.json";
import NavBarButton from "../NavBar/NavBarButton";
import TipsButton from "../TipsButton/TipsButton";
import Tips from "../../models/Tips";
import { AsyncStorageKey, getItemFromAsyncStorage } from "../../utils/AsyncStorage/AsyncStorage";

type SuperPowerBlockContentType = "activity" | "category" | "pdf" | "video" | "audio" | "preview" | "doc" | "multi";
type SuperPowerBlockDisplay = "row" | "path" | "column" | "grid" | "category_grid";

type SuperPowerContent = {
    content_type: SuperPowerBlockContentType;
    key?: string;
    url?: string;
    app_image?: string;
    img_url?: string;
    title?: localizedString;
    sub_contents?: SuperPowerContent[]
}

type SuperPowerBlock = {
    content: SuperPowerContent;
    display: SuperPowerBlockDisplay;
    picto?: string;
    title: localizedString;
}

type TargetSuperPowers = {
    bg: string;
    blocks: {
        all: SuperPowerBlock[]; // All : correspond à l'élémentaire
        cycle1?: SuperPowerBlock[];
        cycle3?: SuperPowerBlock[];
    },
    ill: string;
    title: localizedString;
    next: string;
    order: number;
}

interface SuperPowers {
    all_targets: TargetSuperPowers, // par défaut teachers
    family?: TargetSuperPowers,
    animator?: TargetSuperPowers,
    last_update: number
}

interface SuperPowerSheetProps {
    activities: any;
    localActivities: any;
    localCategories: any;
    componentId: string;
    superpowerKey: string;
    isPageOnScreen: boolean;
    superPowerData: SuperPowers;
    localDocuments: any;
    isModal?: boolean;
}

const SuperPowerSheet = (props: SuperPowerSheetProps) => {

    const appTheme: AppTheme = new AppTheme();
    const images: Images = new Images();
    const user: User = User.getInstance();
    const [viewDidAppear, setViewDidAppear] = useState<boolean>(false);
    const [editionModeValue, setEditionModeValue] = useState(false);
    const [documentKeyToDisplay, setDocumentKeyToDisplay] = useState<{ key: string; title: string; } | undefined>(undefined);
    const [multiContentToDisplay, setMultiContentToDisplay] = useState<SuperPowerContent | undefined>(undefined);
    const [showLevelSelection, setShowLevelSelection] = useState<boolean>(false);
    const [userCycleChoice, setUserCycleChoice] = useState<string | undefined>(user.children_level);

    const actionSheetRef = useRef<ActionSheet>(null);
    const inViewModal = useRef<InViewModal>(null);

    // Il faut prendre en compte que si une ligne a plus de 3 items, on va avoir des boutons pour avancer reculer, donc on rajouter 60 pixels pour les côtés
    // Noramelemnt on a 270 pixels pour les 3 cellules, on va en avoir 210
    let cellsRowWidth: number = Platform.OS === "web" ? appTheme.getBlockWidth() - appTheme.pixelPerfect(60) : appTheme.getFullAppWidth() - appTheme.pixelPerfect(60);
    if (cellsRowWidth > 600) {
        cellsRowWidth = 600;
    }
    const cellWidth: number = cellsRowWidth / 3;
    let imageSize: number = cellWidth - appTheme.pixelPerfect(20);
    if (imageSize > 140) {
        imageSize = 140;
    }

    const { superpowerKey, superPowerData, localCategories, activities, localActivities, componentId, localDocuments, isModal = false } = props;
    let navigate: any = null;
    if (Platform.OS === "web") {
        navigate = useNavigate();
    }

    useEffect(() => {
        if (props.isPageOnScreen === true) {
            getEditionValue();
            setViewDidAppear(true);
        }
    }, [props]);

    const getEditionValue = async () => {
        // On va récupérer les donnnées admin si l'utilisateur est admin
        if (user.admin === true) {
            try {
                const adminDataString = await getItemFromAsyncStorage({ itemKey: AsyncStorageKey.adminData });
                if (adminDataString === null) {
                    setEditionModeValue(false);
                } else {
                    const adminData = adminDataString.data;
                    if (adminData.editionMode !== undefined) {
                        setEditionModeValue(adminData.editionMode);
                    } else {
                        setEditionModeValue(false);
                    }
                }
            } catch (error) {
                setEditionModeValue(false);
            }
        }
    }

    const showActivitySheet = (data: { activityKey: string, categoryKey: string, sectionKey: string, analytics_key: string }) => {
        const { activityKey, categoryKey, sectionKey } = data;
        if (Platform.OS === "web") {
            inViewModal.current?.showAlert({
                name: ScreenActivitySheet,
                passProps: {
                    activityKey,
                    categoryKey,
                    sectionKey,
                    context: user.profile
                }
            });
        } else {
            Navigation.push({
                componentId,
                navigate,
                passProps: {
                    activityKey,
                    categoryKey,
                    sectionKey,
                    context: user.profile
                },
                name: ScreenActivitySheet
            })
        }
    }

    const showPDF = (data: { pdfData: any, sectionKey: string, title: string }) => {
        const { pdfData, sectionKey, title } = data;
        inViewModal.current?.showAlert({
            name: ScreenPDFViewer,
            passProps: {
                sectionId: sectionKey,
                title: title,
                color: appTheme.superPowersColor,
                pdfUrl: pdfData.url
            }
        });
    }

    const showCategory = (categoryKey: string, sectionKey: string) => {
        let passProps = {
            categoryKey,
            section: sectionKey,
            origin: superpowerKey
        }
        Navigation.push({
            componentId,
            navigate,
            name: ScreenCategoryHome,
            passProps
        });
    }

    const renderDocumentModal = (): React.JSX.Element | null => {
        if (documentKeyToDisplay === undefined) {
            return null;
        }
        return <DocumentsViewer title={documentKeyToDisplay.title} documentId={documentKeyToDisplay.key} onDocumentCloseRequest={(() => setDocumentKeyToDisplay(undefined))} />
    }

    const headerCell = (data: { title: string, picto?: string, forcedWidth?: number, index: string }) => {
        const { title, picto, forcedWidth, index } = data;
        const cellWidth: number = forcedWidth !== undefined ? forcedWidth : Platform.OS === "web" ? appTheme.getBlockWidth() : appTheme.getFullAppWidth();
        const borderTopEndRadius = Platform.OS === "web" ? appTheme.pixelPerfect(20) : 0;
        const borderTopStartRadius = Platform.OS === "web" ? appTheme.pixelPerfect(20) : 0;
        const iconSize = Platform.OS === "web" ? appTheme.pixelPerfect(50) : appTheme.pixelPerfect(30);
        const picotView: React.JSX.Element = picto !== undefined ? <Image source={images[picto]} style={{ width: iconSize, height: iconSize, marginEnd: appTheme.pixelPerfect(10) }} resizeMode="contain" /> : <View />;
        return <View key={`header_${index}`} style={{ width: cellWidth, justifyContent: "center", alignItems: "center" }}>
            <View style={{ borderTopEndRadius, borderTopStartRadius, flexDirection: "row", width: cellWidth, backgroundColor: appTheme.superPowersColor, paddingVertical: appTheme.pixelPerfect(10), justifyContent: "center", alignItems: "center" }}>
                {picotView}
                <Text style={{ fontFamily: appTheme.primaryBoldFont, color: appTheme.white, textTransform: "uppercase", fontSize: appTheme.pixelPerfectForFont(10), textAlign: "center" }} >{title}</Text>
            </View>
        </View>
    }

    const rowDisplayCell = (data: { content: SuperPowerContent, index: string, forcedWidth?: number }) => {
        const { content, index, forcedWidth } = data;
        let cellAction: any = undefined;
        let title: string = "";
        let imageSource = undefined;
        let isDraft: boolean = false;
        let borderRadius: number = appTheme.pixelPerfect(10);
        let imageCellStyle: ViewStyle[] = [{ borderRadius }];
        let imageStyle: ImageStyle[] = [{ width: imageSize, height: imageSize, borderRadius }];
        let borderColor: string = appTheme.darkBlue;
        let borderWidth: number = 2;
        let contentType: SuperPowerBlockContentType = content.content_type;
        if (contentType === "multi") {
            imageCellStyle.push(Style.shadowed);
            imageSource = images[content.app_image] !== undefined ? images[content.app_image] : { uri: content.img_url };
            cellAction = () => setMultiContentToDisplay(content);
        } else if (contentType === "activity") {
            imageCellStyle.push(Style.shadowed);
            const activityContent: Activity = localActivities[content.key];
            if (activityContent.status === "draft") {
                if (config.isProduction === false) {
                    isDraft = true;
                } else {
                    return <View />;
                }
            }
            title = activityContent.title[getLanguage()];
            imageSource = images[activityContent.app_image] !== undefined ? images[activityContent.app_image] : { uri: activityContent.img_url };
            cellAction = () => showActivitySheet({ activityKey: activityContent.key, sectionKey: activityContent.section_key, categoryKey: activityContent.category_keys[0], analytics_key: activityContent.analytics_key });
        } else if (contentType === "doc") {
            imageCellStyle.push(Style.shadowed);
            if ((localDocuments.data !== undefined) && (localDocuments.data[content.key] !== undefined)) {
                let showProductionOnly: boolean = true;
                if (config.isProduction === false) {
                    if (editionModeValue === true) {
                        if (localDocuments.data[content.key].draft !== undefined) {
                            showProductionOnly = false;
                        }
                    }
                }
                if ((config.isProduction === true) && (localDocuments.data[content.key].published === undefined)) {
                    return <View />
                } else if ((config.isProduction === false) && (localDocuments.data[content.key].draft === undefined)) {
                    return <View />
                } else if (showProductionOnly === false) {
                    isDraft = true;
                }
            }
            imageSource = images[content.app_image] !== undefined ? images[content.app_image] : { uri: content.img_url };
            cellAction = () => setDocumentKeyToDisplay({ key: content.key, title: content.title[getLanguage()] });
        } else if (contentType === "pdf") {
            imageCellStyle.push(Style.shadowed);
            imageSource = images[content.app_image] !== undefined ? images[content.app_image] : { uri: content.img_url };
            cellAction = () => showPDF({ pdfData: { url: content.url }, sectionKey: "superPowers", title });
        } else if (contentType === "category") {
            const categoryContent: Category = localCategories[content.key];
            borderRadius = imageSize / 2;
            if (categoryContent.status === "draft") {
                if (config.isProduction === false) {
                    isDraft = true;
                } else {
                    return <View />;
                }
            }
            title = categoryContent.title[getLanguage()];
            let icon = undefined;
            let remoteIcon = undefined;
            let activitiesForCategory = undefined;
            let sectionKey = "";
            for (const aSectionKey in activities) {
                if (Object.prototype.hasOwnProperty.call(activities, aSectionKey)) {
                    const aSection = activities[aSectionKey];
                    if (aSection[content.key] !== undefined) {
                        activitiesForCategory = aSection[content.key];
                        sectionKey = aSectionKey;
                        break;
                    }
                }
            }
            let doesCategoryHaveValidActivities: boolean = false;
            for (const anActivityIndex in activitiesForCategory) {
                if (Object.prototype.hasOwnProperty.call(activitiesForCategory, anActivityIndex)) {
                    const anActivityKey: string = activitiesForCategory[anActivityIndex];
                    const anActivity = localActivities[anActivityKey];
                    // On vérifie d'abord que l'activité est taguée avec le super-pouvoir
                    if ((anActivity !== undefined) && (anActivity.tags !== undefined) && (anActivity.tags.indexOf(superpowerKey) !== -1)) {
                        // Puis que l'activité est taguée avec pour le cycle sélectionné
                        if ((anActivity.cycles !== undefined) && (anActivity.cycles.indexOf(userCycleChoice) !== -1)) {
                            icon = anActivity.app_image;
                            remoteIcon = anActivity.img_url;
                            doesCategoryHaveValidActivities = true;
                            break;
                        }

                    }
                }
            }
            if (doesCategoryHaveValidActivities === false) {
                return <View />
            }
            imageSource = icon !== undefined ? images[icon] : remoteIcon !== undefined ? remoteIcon : "";
            cellAction = () => showCategory(content.key, sectionKey);
            borderColor = categoryContent.sectionId === "grow" ? appTheme.grow : categoryContent.sectionId === "talk" ? appTheme.talk : categoryContent.sectionId === "still" ? appTheme.still : categoryContent.sectionId === "inspire" ? appTheme.inspire : borderColor;
            imageStyle.push({ borderWidth, borderColor, borderRadius });
        }
        if ((content.title !== undefined) && (content.title[getLanguage()] !== undefined)) {
            title = content.title[getLanguage()];
        }
        const draftView: React.JSX.Element = isDraft === false ? <View /> : <View style={{ alignItems: "center", position: "absolute", bottom: 0, width: imageSize, backgroundColor: "#00000060", padding: 2, borderBottomEndRadius: appTheme.pixelPerfect(10), borderBottomStartRadius: appTheme.pixelPerfect(10) }}>
            <Text style={{ color: "#ffffff", fontSize: appTheme.pixelPerfectForFont(10) }}>BROUILLON</Text>
        </View>;
        return <View key={`row_${index}`} style={{ backgroundColor: appTheme.white, paddingVertical: appTheme.pixelPerfect(5), paddingHorizontal: appTheme.pixelPerfect(5), width: cellWidth, justifyContent: "flex-start", alignItems: "center" }}>
            <TouchableOpacity onPress={cellAction} style={{ paddingHorizontal: appTheme.pixelPerfect(20), width: cellWidth, paddingVertical: appTheme.pixelPerfect(10), justifyContent: "center", alignItems: "center" }}>
                <View style={imageCellStyle}>
                    <Image nativeID={'image_' + content.key} source={imageSource} style={imageStyle} resizeMode="cover" />
                    {draftView}
                </View>
                <View style={{ width: cellWidth - appTheme.pixelPerfect(10), marginTop: appTheme.pixelPerfect(5) }}>
                    <Text style={{ fontFamily: appTheme.primaryBoldFont, color: borderColor, fontSize: Platform.OS === "web" ? appTheme.pixelPerfectForFont(9) : appTheme.pixelPerfectForFont(8), textAlign: "center" }} >{title}</Text>
                </View>
            </TouchableOpacity>
        </View>
    }

    const pathDisplayCell = (data: { type: number, content: SuperPowerContent, index: string, last_cell?: boolean }) => {
        const { type, content, index, last_cell = false } = data;
        let marginStartForFeet = appTheme.pixelPerfect(50);
        let marginStartForImage = appTheme.pixelPerfect(10);
        let marginTopForImage = appTheme.pixelPerfect(5);
        let marginBottomForImage = appTheme.pixelPerfect(0);
        let angleForFeet = "0deg";
        if (type === 1) {
            marginStartForFeet = appTheme.pixelPerfect(110);
            marginStartForImage = appTheme.pixelPerfect(70);
            marginTopForImage = appTheme.pixelPerfect(0);
            marginBottomForImage = appTheme.pixelPerfect(0);
            angleForFeet = "0deg";
        } else if (type === 2) {
            marginStartForFeet = appTheme.pixelPerfect(150);
            marginStartForImage = appTheme.pixelPerfect(120);
            marginTopForImage = appTheme.pixelPerfect(10);
            marginBottomForImage = appTheme.pixelPerfect(5);
            angleForFeet = "25deg";
        } else if (type === 3) {
            marginStartForFeet = appTheme.pixelPerfect(145);
            marginStartForImage = appTheme.pixelPerfect(145);
            marginTopForImage = appTheme.pixelPerfect(10);
            marginBottomForImage = appTheme.pixelPerfect(5);
            angleForFeet = "45deg";
        } else if (type === 4) {
            marginStartForFeet = appTheme.pixelPerfect(110);
            marginStartForImage = appTheme.pixelPerfect(120);
            marginTopForImage = appTheme.pixelPerfect(10);
            marginBottomForImage = appTheme.pixelPerfect(5);
            angleForFeet = "60deg";
        } else if (type === 5) {
            marginStartForFeet = appTheme.pixelPerfect(60);
            marginStartForImage = appTheme.pixelPerfect(80);
            marginTopForImage = appTheme.pixelPerfect(5);
            marginBottomForImage = appTheme.pixelPerfect(5);
            angleForFeet = "60deg";
        } else if (type === 6) {
            marginStartForFeet = appTheme.pixelPerfect(25);
            marginStartForImage = appTheme.pixelPerfect(25);
            marginTopForImage = appTheme.pixelPerfect(10);
            marginBottomForImage = appTheme.pixelPerfect(10);
            angleForFeet = "50deg";
        }
        const activityContent: Activity = localActivities[content.key];
        const feetImageSize = Platform.OS === "web" ? appTheme.pixelPerfect(50) : appTheme.pixelPerfect(30);
        if (Platform.OS === "web") {
            marginStartForFeet = marginStartForFeet + appTheme.pixelPerfect(20);
        }
        const imageSize = Platform.OS === "web" ? appTheme.pixelPerfect(90) : appTheme.pixelPerfect(50);
        let imageSource = images[activityContent.app_image] !== undefined ? images[activityContent.app_image] : { url: activityContent.img_url };
        let cellAction = () => showActivitySheet({ activityKey: activityContent.key, sectionKey: activityContent.section_key, categoryKey: activityContent.category_keys[0], analytics_key: activityContent.analytics_key });
        let activityCategory: Category = localCategories[activityContent.category_keys[0]];
        const cellColor: string = activityContent.section_key === "grow" ? appTheme.grow : activityContent.section_key === "talk" ? appTheme.talk : activityContent.section_key === "still" ? appTheme.still : activityContent.section_key === "inspire" ? appTheme.inspire : appTheme.darkBlue;
        const footImage = Platform.OS === "web" ? require("../../assets/img/ic_foot.png") : require("../../assets/img/ic_foot.png");
        let footImageView: React.JSX.Element = <View />;
        if (last_cell === false) {
            footImageView = <Image source={footImage} style={{ marginTop: marginTopForImage, marginBottom: marginBottomForImage, transform: [{ rotate: angleForFeet }], marginStart: marginStartForFeet, width: feetImageSize, height: feetImageSize }} resizeMode="contain" />;
        }
        return <View key={`path_${index}`} style={{ width: ((Platform.OS === "web") && (appTheme.getFlexDirectionForSplitScreen() === "row")) ? "60%" : "100%" }}>
            <TouchableOpacity onPress={cellAction} style={{ flexDirection: "row", width: "100%", height: imageSize, alignItems: "center" }}>
                <View style={[Style.shadowed, { marginStart: marginStartForImage, width: imageSize, height: imageSize, borderRadius: appTheme.pixelPerfect(10) }]}>
                    <Image source={imageSource} style={[{ width: imageSize, height: imageSize, borderRadius: appTheme.pixelPerfect(10) }]} resizeMode="cover" />
                </View>
                <View style={{ justifyContent: "center", height: imageSize, flexShrink: 1 }}>
                    <Text numberOfLines={2} style={{ color: appTheme.darkBlue, fontSize: Platform.OS === "web" ? appTheme.pixelPerfectForFont(9) : appTheme.pixelPerfectForFont(10), fontFamily: appTheme.primaryBoldFont, paddingHorizontal: appTheme.pixelPerfect(5) }}>{activityContent.title[getLanguage()]}</Text>
                    <Text numberOfLines={2} style={{ color: cellColor, fontSize: Platform.OS === "web" ? appTheme.pixelPerfectForFont(9) : appTheme.pixelPerfectForFont(10), fontFamily: appTheme.primaryBoldFont, paddingHorizontal: appTheme.pixelPerfect(5) }}>{activityCategory.title[getLanguage()]}</Text>
                </View>
            </TouchableOpacity>
            {footImageView}
        </View>
    }

    const footerCell = () => {
        if (Platform.OS === "web") {
            return <View style={{ height: appTheme.pixelPerfect(20), backgroundColor: appTheme.white, width: appTheme.getBlockWidth(), borderBottomStartRadius: appTheme.pixelPerfect(20), borderBottomEndRadius: appTheme.pixelPerfect(20), marginBottom: appTheme.pixelPerfect(20) }} />
        }
        return <View />
    }

    const renderBlocks = () => {
        let blockCell: React.JSX.Element[] = [];
        // Par défaut, on charge les blocls all_targets, qui sont les définitions des blocs pour le niveau élémentaire, et pour all, qui est la valeur par défaut pour les profs On regarde alors s'il y a des blocks spécifiques pour les maternelles (cycle1) ou pour les collégiens (cycle3)
        const { profile, children_level } = user;
        let blocksByTarget: TargetSuperPowers = superPowerData.all_targets;
        if (superPowerData[profile] !== undefined) {
            blocksByTarget = superPowerData[profile];
        }
        let blocks: SuperPowerBlock[] = blocksByTarget.blocks.all;
        if (blocksByTarget.blocks[children_level] !== undefined) {
            blocks = blocksByTarget.blocks[children_level];
        }
        for (const aBlockIndex in blocks) {
            if (Object.prototype.hasOwnProperty.call(blocks, aBlockIndex)) {
                const aBlock: SuperPowerBlock = blocks[aBlockIndex];
                // On va d'abord afficher le titre
                blockCell.push(headerCell({ title: aBlock.title[getLanguage()], picto: aBlock.picto, index: aBlockIndex }));
                // Le contenu du bloc peut dépendre du niveau des élèves visés. On va donc charger celui par défaut "all" qui corrspondant aux enseignants, puis regarder en fonction du profil de l'utilisateur (family ou animator) s'il y a un contenu spécifique
                let blockContent: SuperPowerContent[] = aBlock.content;
                if (aBlock.display === "row") {
                    let rowDisplayCells: React.JSX.Element[] = [];
                    for (const aContentIndex in blockContent) {
                        if (Object.prototype.hasOwnProperty.call(blockContent, aContentIndex)) {
                            const aSuperPowerContent: SuperPowerContent = blockContent[aContentIndex];
                            rowDisplayCells.push(rowDisplayCell({ content: aSuperPowerContent, index: `${aBlockIndex}_${aContentIndex}` }));
                        }
                    }
                    blockCell.push(<SuperPowerRow
                        key={aBlockIndex}
                        containerWidth={Platform.OS === "web" ? appTheme.getBlockWidth() : appTheme.getFullAppWidth()}
                        rowWidth={cellsRowWidth}
                        rowBlocks={rowDisplayCells} />
                    )
                } else if (aBlock.display === "path") {
                    //On va boucler sur les activités et leur donner un type
                    let pathDisplayCells: React.JSX.Element[] = [];
                    let type: number = 0;
                    for (const aContentIndex in blockContent) {
                        if (Object.prototype.hasOwnProperty.call(blockContent, aContentIndex)) {
                            const aSuperPowerContent: SuperPowerContent = blockContent[aContentIndex];
                            pathDisplayCells.push(pathDisplayCell({ content: aSuperPowerContent, type, index: `${aBlockIndex}_${aContentIndex}`, last_cell: (parseInt(aContentIndex) === blockContent.length - 1) }));
                            if (type < 6) {
                                type++;
                            } else {
                                type = 0;
                            }
                        }
                    }
                    blockCell.push(<View style={{ width: Platform.OS === "web" ? appTheme.getBlockWidth() : appTheme.getFullAppWidth(), paddingVertical: appTheme.pixelPerfect(20), backgroundColor: "#FFFFFF", alignItems: "center" }}>
                        {pathDisplayCells}
                    </View>);
                } else if (aBlock.display === "grid") {
                    let rowDisplayCells: React.JSX.Element[] = [];
                    for (const aContentIndex in blockContent) {
                        if (Object.prototype.hasOwnProperty.call(blockContent, aContentIndex)) {
                            const aSuperPowerContent: SuperPowerContent = blockContent[aContentIndex];
                            rowDisplayCells.push(rowDisplayCell({ content: aSuperPowerContent, index: `${aBlockIndex}_${aContentIndex}` }));
                        }
                    }
                    blockCell.push(<View style={{ width: Platform.OS === "web" ? appTheme.getBlockWidth() : appTheme.getFullAppWidth(), backgroundColor: "#FFFFFF", alignItems: "center" }}>
                        <View style={{ width: cellsRowWidth, flexDirection: "row", backgroundColor: "#ffffff", justifyContent: "flex-start", flexWrap: "wrap" }}>
                            {rowDisplayCells}
                        </View>
                    </View>
                    )
                } else if (aBlock.display === "category_grid") {
                    // On va afficher toutes les catégories qui ont au moins une activité qui correspond au niveau de l'enfant et aux tags sur super-pouvoir
                    let rowDisplayCells: React.JSX.Element[] = [];
                    let inspireRowDisplayCells: React.JSX.Element[] = [];
                    let stillRowDisplayCells: React.JSX.Element[] = [];
                    let talkeRowDisplayCells: React.JSX.Element[] = [];
                    let growRowDisplayCells: React.JSX.Element[] = [];
                    for (const aCategoryIndex in localCategories) {
                        if (Object.prototype.hasOwnProperty.call(localCategories, aCategoryIndex)) {
                            const aCategory: Category = localCategories[aCategoryIndex];
                            // On va déjà filtrer pour n'afficher que les catégories des sections Je me pose ("still"), Je m'exprime ("talk"), Je grandis ("grow") et Je m'inspire ("inspire").
                            if ((aCategory.sectionId === "still") || (aCategory.sectionId === "talk") || (aCategory.sectionId === "grow") || (aCategory.sectionId === "inspire")) {
                                if ((aCategory.status === "published") && ((aCategory.minBuildNumber === undefined) || (aCategory.minBuildNumber < parseInt(build)))) {
                                    if (aCategory.sectionId === "inspire") {
                                        inspireRowDisplayCells.push(rowDisplayCell({ content: { key: aCategory.key, content_type: "category" }, index: `${aBlockIndex}_${aCategory.key}` }));
                                    } else if (aCategory.sectionId === "still") {
                                        stillRowDisplayCells.push(rowDisplayCell({ content: { key: aCategory.key, content_type: "category" }, index: `${aBlockIndex}_${aCategory.key}` }));
                                    } else if (aCategory.sectionId === "talk") {
                                        talkeRowDisplayCells.push(rowDisplayCell({ content: { key: aCategory.key, content_type: "category" }, index: `${aBlockIndex}_${aCategory.key}` }));
                                    } else if (aCategory.sectionId === "grow") {
                                        growRowDisplayCells.push(rowDisplayCell({ content: { key: aCategory.key, content_type: "category" }, index: `${aBlockIndex}_${aCategory.key}` }));
                                    }
                                }
                            }
                        }
                    }
                    rowDisplayCells = inspireRowDisplayCells.concat(stillRowDisplayCells.concat(talkeRowDisplayCells.concat(growRowDisplayCells)));
                    blockCell.push(<View style={{ width: Platform.OS === "web" ? appTheme.getBlockWidth() : appTheme.getFullAppWidth(), backgroundColor: "#FFFFFF", alignItems: "center" }}>
                        <View style={{ width: cellsRowWidth, flexDirection: "row", backgroundColor: "#ffffff", justifyContent: "flex-start", flexWrap: "wrap" }}>
                            {rowDisplayCells}
                        </View>
                    </View>
                    )
                }
                // On va fermer le block avec un arrondi
                blockCell.push(footerCell());
            }
        }
        return <View style={{ width: appTheme.getFullScreenWidth(), alignItems: "center" }}>
            {blockCell}
        </View>;
    }

    const goBack = () => {
        if (isModal === false) {
            Navigation.pop({
                componentId,
                navigate
            });
        } else {
            Navigation.dismissModal({
                componentId,
                navigate
            })
        }
    }

    const renderContent = () => {
        if (props.isPageOnScreen === false) {
            return <View style={{ width: appTheme.getFullAppWidth(), height: appTheme.getFullAppHeight(), justifyContent: "center", alignItems: "center" }}>
                <ActivityIndicator loading={true} color={appTheme.white} />
            </View>
        }
        if (viewDidAppear === false) {
            return <View style={{ width: appTheme.getFullAppWidth(), height: appTheme.getFullAppHeight(), justifyContent: "center", alignItems: "center" }}>
                <Text style={{ color: "#FFF" }}>{superpowerKey}</Text>
                <ActivityIndicator loading={true} color={appTheme.white} />
            </View>
        }
        let imageWidth = appTheme.getFullAppWidth();
        if (imageWidth > 1920) {
            imageWidth = 1920;
        }
        const imageHeight = Platform.OS === "web" ? (imageWidth * 900 / 1920) / 2 : imageWidth * 156 / 360;
        const blockMarginTop: number = Platform.OS === "web" ? -imageHeight * 2 / 7 : 0;
        const pictoBottomPosition: number = Platform.OS === "web" ? -blockMarginTop - appTheme.pixelPerfect(25) : -appTheme.pixelPerfect(25);
        return <View style={{ width: appTheme.getFullAppWidth(), height: appTheme.getFullAppHeight(), justifyContent: "flex-start", alignItems: "center" }}>
            <ScrollView style={{ flex: 1, }}>
                <View style={{ backgroundColor: appTheme.darkBlue, width: appTheme.getFullAppWidth(), alignItems: "center" }}>
                    <View style={{ alignItems: "center", width: appTheme.getFullAppWidth() }}>
                        <Image source={images[superPowerData.all_targets.bg]} style={{ backgroundColor: appTheme.darkBlue, width: imageWidth, height: imageHeight }} resizeMode="cover" />
                        <View style={{ position: "absolute", bottom: pictoBottomPosition, width: appTheme.pixelPerfect(50), height: appTheme.pixelPerfect(50), borderRadius: appTheme.pixelPerfect(25), backgroundColor: "#354F84", paddingTop: appTheme.pixelPerfect(2) }}>
                            <Image source={images[superPowerData.all_targets.ill]} style={{ width: appTheme.pixelPerfect(50), height: appTheme.pixelPerfect(23) }} resizeMode="contain" />
                        </View>
                    </View>
                    <View style={{ marginTop: blockMarginTop }}>
                        {renderBlocks()}
                    </View>
                    <View style={{ height: appTheme.bottomInsets + appTheme.pixelPerfect(80) }} />
                </View>
            </ScrollView>
        </View>
    }


    const renderNavBar = () => {
        const titlesHorizontalAlign: string = Platform.OS === "web" ? "center" : "flex-start";
        const titleSuperHeroFontSize: number = Platform.OS === "web" ? appTheme.pixelPerfectForFont(11) : appTheme.pixelPerfectForFont(9);
        const navBarBackgroundColor: string = Platform.OS === "web" ? appTheme.darkBlue : appTheme.superPowersColor;
        let tipsButton: React.JSX.Element = <View />;
        const tips: Tips = Tips.getInstance();
        if (tips.superpowers !== undefined) {
            if (tips.superpowers[superpowerKey] !== undefined) {
                tipsButton = <TipsButton color={appTheme.superPowersColor} tips={tips.superpowers[superpowerKey]} style={{ marginHorizontal: appTheme.pixelPerfect(10) }} />;
            }
        }
        const levelTitle: string = user.profile === "teacher" ? i18n.t("levels." + user.children_level) : i18n.t("filters." + user.children_level);
        let navBarWidth: number = Platform.OS === "web" ? appTheme.getBlockWidth() - appTheme.pixelPerfect(20) : appTheme.getFullAppWidth() - appTheme.pixelPerfect(10);
        return <View style={{ width: appTheme.getFullAppWidth(), paddingTop: appTheme.topInsets + 10, alignItems: "center", paddingHorizontal: appTheme.pixelPerfect(5), backgroundColor: navBarBackgroundColor }}>
            <View style={{ flexDirection: "row", width: navBarWidth, alignItems: "center", height: appTheme.pixelPerfect(60) }}>
                <NavBarButton style={{ marginEnd: appTheme.pixelPerfect(10) }} icon={isModal === false ? IcBack : IcClose} action={goBack} />
                <View style={{ flex: 1, alignItems: titlesHorizontalAlign }}>
                    <Text style={{ color: appTheme.white, fontFamily: appTheme.superHerosFontBold, fontSize: titleSuperHeroFontSize }}>{superPowerData.all_targets.title[getLanguage()]}</Text>
                    <Text style={{ color: appTheme.white, fontFamily: appTheme.primarySemiBoldFont, fontSize: appTheme.pixelPerfectForFont(7) }}>{levelTitle.toLocaleLowerCase()}</Text>
                </View>
                {tipsButton}
                <NavBarButton icon={IcFilter} action={() => setShowLevelSelection(true)} />
            </View>
        </View>
    }

    const onToggleFilters = () => {
        setShowLevelSelection(false);
        if (user.children_level !== userCycleChoice) {
            setUserCycleChoice(user.children_level);
        }
    }

    const renderMultiContentChoices = () => {
        if (multiContentToDisplay === undefined) {
            return <View />
        }
        let choicesTitle: string = multiContentToDisplay.title[getLanguage()];
        let choicesView: React.JSX.Element[] = [];
        const width: number = Platform.OS === "web" ? appTheme.getBlockWidth() : appTheme.getFullAppWidth();
        const marginHorizontal = appTheme.pixelPerfect(20);
        const buttonWidth = width - marginHorizontal * 2;
        const buttonPadding = appTheme.pixelPerfect(5);
        const iconWidth = appTheme.pixelPerfect(40);
        const textWidth = buttonWidth - iconWidth - 2 * buttonPadding;
        const buttonFontSize = appTheme.pixelPerfectForFont(12);
        const buttonHeight = appTheme.pixelPerfect(64);
        for (const aChoiceIndex in multiContentToDisplay.sub_contents) {
            if (Object.prototype.hasOwnProperty.call(multiContentToDisplay.sub_contents, aChoiceIndex)) {
                const aContent: SuperPowerContent = multiContentToDisplay.sub_contents[aChoiceIndex];
                if (aContent.content_type === "doc") {
                    choicesView.push(<TouchableOpacity key="main" onPress={() => {
                        setMultiContentToDisplay(undefined);
                        setDocumentKeyToDisplay({ key: aContent.key, title: aContent.title[getLanguage()] });
                    }} style={{ borderWidth: 1, borderColor: appTheme.darkBlue, paddingHorizontal: buttonPadding, flexDirection: "row", alignItems: "center", justifyContent: "space-around", backgroundColor: appTheme.white, width: buttonWidth, height: buttonHeight, borderRadius: appTheme.pixelPerfect(20), marginBottom: appTheme.pixelPerfect(20) }}>
                        <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
                            <Text numberOfLines={3} style={{ textAlign: "center", fontFamily: appTheme.secondaryFont, fontSize: buttonFontSize, color: appTheme.darkBlue, width: textWidth }}>{aContent.title[getLanguage()].toLocaleUpperCase()}</Text>
                        </View>
                    </TouchableOpacity>);
                }
            }
        }
        const windowMarginBottom = Platform.OS === "web" ? appTheme.pixelPerfect(20) : 0;
        return <View style={{ width: appTheme.getFullScreenWidth(), height: appTheme.getFullScreenHeight(), justifyContent: "flex-end", alignItems: "center", backgroundColor: appTheme.darkBlue + "80", paddingLeft: appTheme.webNavBarWidth }}>
            <View style={[Style.shadowed, { paddingTop: appTheme.pixelPerfect(10), backgroundColor: appTheme.superPowersColor, width, borderRadius: appTheme.pixelPerfect(10), alignItems: "center", marginBottom: windowMarginBottom }]}>
                <View style={{ marginBottom: appTheme.pixelPerfect(20), width: "100%", paddingHorizontal: appTheme.pixelPerfect(10), alignItems: "flex-end" }}>
                    <TouchableOpacity onPress={() => setMultiContentToDisplay(undefined)} style={{ backgroundColor: appTheme.white, width: appTheme.pixelPerfect(30), height: appTheme.pixelPerfect(30), borderRadius: appTheme.pixelPerfect(15), justifyContent: "center", alignItems: "center" }}>
                        <SVGView Component={IcClose} size={20} color={appTheme.superPowersColor} />
                    </TouchableOpacity>
                </View>
                <Text style={{ paddingHorizontal: appTheme.pixelPerfect(20), textAlign: "center", fontFamily: appTheme.primarySemiBoldFont, fontSize: appTheme.pixelPerfectForFont(16), color: appTheme.white }}>
                    {choicesTitle}
                </Text>
                <View style={{ marginVertical: appTheme.pixelPerfect(20) }}>
                    {choicesView}
                </View>
            </View>
        </View>;
    }

    return <View style={{ width: appTheme.getFullAppWidth(), flex: 1, backgroundColor: appTheme.darkBlue }}>
        {renderNavBar()}
        {renderContent()}
        <ActionSheet textColor={appTheme.white} backgroundColor={"#354F84"} componentId={componentId} ref={actionSheetRef} />
        <InViewModal ref={inViewModal} />
        <Modal
            transparent={true}
            animationType="fade"
            visible={multiContentToDisplay !== undefined}
            style={{ flex: 1, justifyContent: "flex-end" }}>
            {renderMultiContentChoices()}
        </Modal>
        <Modal animationType="slide" visible={documentKeyToDisplay !== undefined} transparent={true} style={{ flex: 1 }}>
            <View style={{
                paddingLeft: appTheme.webNavBarWidth,
                flex: 1,
                alignItems: "center",
                backgroundColor: appTheme.superPowersColor
            }}>
                {renderDocumentModal()}
            </View>
        </Modal>
        <UserLevelSelection
            visible={showLevelSelection}
            style={{ flex: 1, justifyContent: "flex-end" }}
            backgroundColor={appTheme.schoolColor}
            toggleFilters={onToggleFilters} />
    </View>
}

export default SuperPowerSheet;